import { useState } from "react";
import type { OAuthGrant } from "@supabase/supabase-js";
import { useAuth } from "@/core/auth/hooks/useAuth";
import { Button } from "@/shared/components/shadcn/Button";
import { ConfirmationDialog } from "@/shared/components/ui/ConfirmationDialog";
import { EmptyState } from "@/shared/components/ui/EmptyState";
import { ErrorState } from "@/shared/components/ui/ErrorState";
import { LoadingSpinner } from "@/shared/components/ui/LoadingSpinner";
import { Page, PageContent } from "@/shared/components/ui/Page";
import { PageHeader } from "@/shared/components/ui/PageHeader";
import { Section } from "@/shared/components/ui/Section";
import { OAuthScopes } from "../components/OAuthScopes";
import { useOAuthGrants, useRevokeOAuthGrant } from "../hooks/oauthQueries";
import { useOAuthOperation } from "../hooks/useOAuthOperation";
import { isSupportedMcpClient, supportsOAuthManagement } from "../services/oauthService";

const revocationNotice = "This removes this app's grant for your entire Froppio account and prevents future token refresh. New Froppio MCP requests will be blocked; in-flight requests may finish. Information already shared with the app is not deleted. Other services may accept already-issued access tokens until they expire.";

function ConnectedApps({ userId }: { userId: string }) {
  const query = useOAuthGrants(userId);
  const revoke = useRevokeOAuthGrant();
  const beginOperation = useOAuthOperation();
  const [selected, setSelected] = useState<OAuthGrant | null>(null);
  const [message, setMessage] = useState<string>();
  if (query.isPending) return <LoadingSpinner message="Loading connected apps..." />;
  if (query.error) return <ErrorState title="Couldn't load connected apps" message="Your authorization provider is unavailable. Please try again." onRetry={() => void query.refetch()} />;
  return <div className="space-y-6">
    <p className="text-sm text-muted-foreground">These are your account's Supabase OAuth grants, shared across your businesses. Disconnecting a grant does not delete any business data.</p>
    {message && <p role="status" className="text-sm">{message}</p>}
    {query.data.length === 0 ? <EmptyState title="No connected apps" message="Apps you authorize will appear here." /> :
      query.data.map(grant => <Section key={grant.client.id} title={grant.client.name} card>
        <p className="break-all text-sm text-muted-foreground">Client ID: {grant.client.id}</p>
        <p className="text-sm">{isSupportedMcpClient(grant.client.id) ? "Supported Froppio MCP client — read-only business access" : "Other OAuth grant — not an approved Froppio MCP client"}</p>
        <OAuthScopes scopes={grant.scopes} />
        <Button variant="outline" disabled={revoke.isPending} onClick={() => { revoke.reset(); setSelected(grant); }}>Disconnect {grant.client.name}</Button>
      </Section>)}
    <Button variant="outline" disabled={query.isFetching || revoke.isPending} onClick={() => void query.refetch()}>Refresh connected apps</Button>
    <ConfirmationDialog open={!!selected} onOpenChange={open => { if (!open) setSelected(null); }}
      title={`Disconnect ${selected?.client.name ?? "app"}?`} description={revocationNotice}
      confirmLabel="Disconnect" dismissLabel="Keep connected" destructive pending={revoke.isPending}
      error={revoke.error ? "Couldn't disconnect this app. Please try again." : undefined}
      onConfirm={() => {
        if (!selected || revoke.isPending) return;
        const isCurrent = beginOperation();
        revoke.mutate(selected.client.id, { onSuccess: () => {
          if (!isCurrent()) return;
          setSelected(null);
          setMessage("App disconnected. New Froppio MCP requests are blocked; in-flight requests may finish. Information already shared with the app is not deleted.");
          void query.refetch();
        } });
      }} />
  </div>;
}

export function ConnectedAppsPage() {
  const { user } = useAuth();
  return <Page><PageHeader title="Connected apps" /><PageContent>
    {!supportsOAuthManagement() ? <ErrorState title="Use your browser" message="Manage connected apps in the standalone Froppio browser app. Native and embedded authentication remain managed by their host." /> :
      user && <ConnectedApps key={user.id} userId={user.id} />}
  </PageContent></Page>;
}
